import { ref, computed } from 'vue'
import { consola } from 'consola'
import { invokeQueuedNotificationAction } from './notificationActionBridge'

const logger = consola.withTag('app-readiness')

/**
 * Флаги готовности различных частей приложения
 */
interface AppReadinessState {
  router: boolean
  products: boolean
  user: boolean
  cart: boolean
}

const readinessState = ref<AppReadinessState>({
  router: false,
  products: false,
  user: false,
  cart: false,
})

/**
 * Очередь отложенных действий (для уведомлений при холодном старте)
 */
interface PendingAction {
  id: string
  data: any
  timestamp: number
}

const pendingActions = ref<PendingAction[]>([])

/**
 * Максимальное время ожидания готовности (мс)
 */
const MAX_WAIT_TIME = 10_000 // 10 секунд

/**
 * Composable для управления готовностью приложения
 * Решает проблему race condition при холодном старте
 */
export function useAppReadiness() {
  /**
   * Проверка полной готовности приложения
   */
  const isFullyReady = computed(() => {
    return readinessState.value.router && readinessState.value.products
  })

  /**
   * Проверка готовности для базовой навигации
   */
  const isNavigationReady = computed(() => {
    return readinessState.value.router
  })

  /**
   * Проверка готовности для работы с продуктами
   */
  const isProductsReady = computed(() => {
    return readinessState.value.products
  })

  /**
   * Установить готовность компонента
   */
  function setReady(component: keyof AppReadinessState, value: boolean = true) {
    readinessState.value[component] = value
    logger.info(`✅ ${component} is ready`, readinessState.value)
    
    // Если приложение полностью готово, обрабатываем отложенные действия
    if (isFullyReady.value && pendingActions.value.length > 0) {
      processPendingActions()
    }
  }

  /**
   * Ожидание готовности приложения
   */
  function waitForReady(
    requiredComponents: Array<keyof AppReadinessState> = ['router', 'products'],
    timeout: number = MAX_WAIT_TIME,
  ): Promise<boolean> {
    const startTime = Date.now()

    return new Promise((resolve) => {
      const checkReadiness = () => {
        const allReady = requiredComponents.every(
          component => readinessState.value[component],
        )

        if (allReady) {
          logger.success('App is ready for action')
          resolve(true)
          return
        }

        const elapsed = Date.now() - startTime
        if (elapsed >= timeout) {
          logger.warn('Timeout waiting for app readiness', {
            elapsed,
            state: readinessState.value,
          })
          resolve(false)
          return
        }

        // Проверяем снова через 100ms
        setTimeout(checkReadiness, 100)
      }

      checkReadiness()
    })
  }

  /**
   * Добавить действие в очередь
   */
  function queueAction(data: any): string {
    const actionId = `action_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`
    
    pendingActions.value.push({
      id: actionId,
      data,
      timestamp: Date.now(),
    })

    logger.info('🔄 Action queued', {
      id: actionId,
      queueLength: pendingActions.value.length,
    })

    return actionId
  }

  /**
   * Обработать отложенные действия
   */
  async function processPendingActions() {
    if (pendingActions.value.length === 0) return

    logger.info('📥 Processing pending actions', {
      count: pendingActions.value.length,
    })

    const actions = [...pendingActions.value]
    pendingActions.value = []

    for (const action of actions) {
      try {
        logger.info('🎯 Processing queued action', { id: action.id })
        await invokeQueuedNotificationAction(action.data)
        
      } catch (error) {
        logger.error('Error processing pending action', {
          id: action.id,
          error,
        })
      }
    }
  }

  /**
   * Очистить очередь
   */
  function clearQueue() {
    const count = pendingActions.value.length
    pendingActions.value = []
    logger.info('🗑️ Queue cleared', { clearedCount: count })
  }

  /**
   * Сброс состояния (для тестирования)
   */
  function reset() {
    readinessState.value = {
      router: false,
      products: false,
      user: false,
      cart: false,
    }
    pendingActions.value = []
    logger.info('🔄 App readiness state reset')
  }

  /**
   * Получить текущее состояние
   */
  function getState() {
    return {
      readiness: { ...readinessState.value },
      pendingActionsCount: pendingActions.value.length,
      isFullyReady: isFullyReady.value,
      isNavigationReady: isNavigationReady.value,
      isProductsReady: isProductsReady.value,
    }
  }

  return {
    // Computed
    isFullyReady,
    isNavigationReady,
    isProductsReady,
    
    // Methods
    setReady,
    waitForReady,
    queueAction,
    processPendingActions,
    clearQueue,
    reset,
    getState,
  }
}



